import { JSX, useState, useRef, useCallback, useEffect } from 'react';
import classNames from 'classnames';
import {
    timersRegistry,
    ITimerSubscribeOptions,
    calcSecondsLeft,
    useAfterUpdate,
} from '@betgames/bg-tools';
import { EMPTY_SPACE, DEFAULT_TIMER_FORMAT } from '@bg-shared';
import classes from './Timer.module.scss';

interface ICommonProps {
    textOnFinish?: string;
    blinkOn?: number;
    className?: string;
}

interface ISingleTimerProps extends ITimerSubscribeOptions, ICommonProps {
    until: number;
    timestamp: number;
}

interface ISharedTimerProps extends ITimerSubscribeOptions, ICommonProps {
    id: number | string;
}

type IProps = ISharedTimerProps | ISingleTimerProps;

export const Timer = ({
    textOnFinish = EMPTY_SPACE,
    blinkOn = 0,
    format = DEFAULT_TIMER_FORMAT,
    ...props
}: IProps): JSX.Element => {
    const [blink, setBlink] = useState<boolean>(false);
    const spanRef = useRef<HTMLSpanElement>();
    const timer = useRef<ReturnType<typeof timersRegistry.create>>();
    const singleProps = props as ISingleTimerProps;
    const sharedProps = props as ISharedTimerProps;

    const setHtml = useCallback((html: string): void => {
        if (spanRef.current) {
            spanRef.current.innerHTML = html;
        }
    }, []);

    const onTick = useCallback((timeString: string, secondsLeft: number): void => {
        setHtml(timeString);

        if (props.onTick) {
            props.onTick(timeString, secondsLeft);
        }
    }, []);

    useEffect(() => {
        setHtml(textOnFinish);

        const onAfterExactSecond = blinkOn
            ? {
                  second: blinkOn,
                  callback: () => {
                      setBlink(true);
                  },
              }
            : props.onAfterExactSecond;

        const onFinish = (): void => {
            setHtml(textOnFinish);
            setBlink(false);

            if (props.onFinish) {
                props.onFinish();
            }
        };

        const options: ITimerSubscribeOptions = {
            format,
            onTick,
            onFinish,
            onAfterExactSecond,
            onUpdate: props.onUpdate,
            showDurationUnits: props.showDurationUnits,
        };

        let unsubscribe = () => {};

        if (sharedProps.id) {
            unsubscribe = timersRegistry.get(sharedProps.id)?.subscribe(options);
        } else {
            timer.current = timersRegistry.create({
                ...options,
                seconds: calcSecondsLeft(singleProps.until, singleProps.timestamp),
            });
        }

        return () => {
            unsubscribe?.();
            timer.current?.destroy();
        };
    }, []);

    useAfterUpdate(() => {
        timer.current?.instance.update(calcSecondsLeft(singleProps.until, singleProps.timestamp));
    }, [singleProps.timestamp]);

    return (
        <span
            ref={spanRef}
            className={classNames(props.className, {
                [classes.blink]: blink,
            })}
            data-qa="text-time-string"
        />
    );
};
